import React, { useEffect, useState, useMemo, useCallback } from "react";
import { TabContainer, TabContent, TabPane, Nav } from "react-bootstrap";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getUrlDetailsObj,
  getUserStatsObject,
  recordActivity,
} from "../../../services/commonService";
import { useUser } from "../../../context/UserContext";
import { COMPANY_LOGO_BASE_URL } from "../../../config/apiConfig";
import { useDispatch } from "react-redux";
import { fetchRevenueAnalysisRequest } from "../../../store/features/actions/fetchRevenueAnalysisAction";
import { fetchReGeoWisevenueAnalysisRequest } from "../../../store/features/actions/fetchGeoWiseRevenueAnalysisAction";
// import { fetchCompFinancialRequest } from "../../../store/features/actions/companyFinancialsActions";
import { fetchSegmentsRequest } from "../../../store/features/actions/segmentsActions";
import { fetchKeyConversationRequest } from "../../../store/features/actions/keyConversationActions";
import CompanyOverview from "./CompanyOverview";
import GrowthOpportunities from "./GrowthOpportunities/GrowthOpportunities";
import NudgesAndEvents from "./NudgesAndEvents/NudgesAndEvents";
// import QuestionsPromtersTab from "./Questions/QuestionsPromtersTab";
import Contacts from "./Contact/Contacts";
// import Financials from "./Financials/Financials";
import CompanyLogoSkeleton from "../skeletons/companyLogoSkeleton";
import { fetchAccountContactRequest } from "../../../store/features/actions/accountContactsActions";
import { fetchDynamicTabsRequest } from "../../../store/features/actions/dynamicTabActions";
import { setGlobalValue } from "../../../store/features/actions/commonActions";
import CustomTabs from "./CustomTabs/CustomTabs";
import MaximisePipeComp from "./Opportunities/MaximisePipeComp";

function AccountDetailsTabs() {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { account_info, account_info_loading } = useSelector(
    (state) => state.accountsReducer
  );
  const { account_contacts, account_contacts_loading } = useSelector(
    (state) => state.accountContactsReducer
  );
  const { dynamicTabsloading, dynamicTabsData, dynamicTabsError } = useSelector(
    (state) => state.dynamicTabsReducer
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = "overview";
  const activeTabFromUrl = searchParams.get("tab") || defaultTab;
  const [activeParentTabKey, setParentActiveTab] = useState(activeTabFromUrl);
  const [accountContactList, setAccountContactList] = useState([]);

  const { clientName, accountId, accountName } = useParams();
  const pageParsedData = getUrlDetailsObj(useLocation, useParams);

  useEffect(() => {
    dispatch(fetchDynamicTabsRequest({ clientName, accountName }));
  }, []);

  // Debounce URL updates
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchParams({ tab: activeParentTabKey });
    }, 100);
    return () => clearTimeout(debounceTimer);
  }, [activeParentTabKey, setSearchParams]);

  // Sync active tab with URL
  useEffect(() => {
    if (activeTabFromUrl !== activeParentTabKey) {
      setParentActiveTab(activeTabFromUrl);
    }
  }, [activeTabFromUrl]);

  useEffect(() => {
    //make API calls which are being used in multiple screens
    if (account_info?.ticker) {
      dispatch(fetchRevenueAnalysisRequest({ ticker: account_info?.ticker }));
      dispatch(
        fetchReGeoWisevenueAnalysisRequest({ ticker: account_info?.ticker })
      );
    }
  }, [account_info?.ticker]);

  useEffect(() => {
    if (accountName) {
      dispatch(fetchSegmentsRequest({ accountName }));
      dispatch(fetchKeyConversationRequest({ accountName }));
      // dispatch(fetchCompFinancialRequest({ accountName }));
    }
    if (
      accountName &&
      (activeParentTabKey == "business" || activeParentTabKey == "contacts")
    ) {
      dispatch(fetchAccountContactRequest(accountName));
    }
  }, [accountName, activeParentTabKey]);

  useEffect(() => {
    const tabTypeMap = {
      overview: "Overview",
      business: "Business",
      growth: "Growth Opportunities",
      nudges_and_events: "Nudges & Events",
      // questions: "Prompters",
      contacts: "Contacts",
      documents: "Documents",
      competitor_insights: "Competitor Insights",
      technology: "Tech Landscape",
      // financial: "Financials",
      agents: "Agents",
      opportunities: "Opportunities",
    };

    const tabType = tabTypeMap[activeParentTabKey];
    if (tabType) {
      pageParsedData.query_params = { tab: activeParentTabKey };
      const userStat = getUserStatsObject(
        tabType,
        "page_load",
        `Page Source: ${accountName}`,
        window.location.href,
        accountId
      );
      userStat.url_details = pageParsedData;
      recordActivity(userStat);
    }
  }, [activeParentTabKey, accountName, accountId, pageParsedData]);

  // Update account contact list
  useEffect(() => {
    if (user?.company_id && !account_contacts_loading) {
      setAccountContactList(account_contacts || { records: [], filters: {} });
    }
  }, [user?.company_id, account_contacts, account_contacts_loading]);

  // Memoized tabs configuration
  const tabs = useMemo(
    () => [
      {
        eventKey: "overview",
        title: "Overview",
        tab_icon: "grid.png",
        content:
          activeParentTabKey === "overview" ? (
            <CompanyOverview active_tab_key={activeParentTabKey} />
          ) : null,
      },
      {
        eventKey: "growth",
        title: "Growth Opportunities",
        tab_icon: "trending-up-3x.png",
        content:
          activeParentTabKey === "growth" ? (
            <GrowthOpportunities active_tab_key={activeParentTabKey} />
          ) : null,
      },
      {
        eventKey: "nudges_and_events",
        title: "Nudges & Events",
        tab_icon: "zap.png",
        content:
          activeParentTabKey === "nudges_and_events" ? (
            <NudgesAndEvents active_tab_key={activeParentTabKey} />
          ) : null,
      },
      {
        eventKey: "contacts",
        title: "Contacts",
        tab_icon: "contact.png",
        content:
          activeParentTabKey === "contacts" ? (
            <Contacts
              active_tab_key={activeParentTabKey}
              account_contacts_data={accountContactList}
              loader={account_contacts_loading}
            />
          ) : null,
      },
      {
        eventKey: "opportunities",
        title: "Opportunities",

        content:
          activeParentTabKey === "opportunities" ? <MaximisePipeComp /> : null,
      },
      // {
      //   eventKey: "financial",
      //   title: "Financial",
      //   tab_icon: "tech-icon.png",
      //   content:
      //     activeParentTabKey === "financial" ? (
      //       <Financials active_tab_key={activeParentTabKey} />
      //     ) : null,
      // },
      {
        eventKey: "custom_tabs",
        title: "Billing Information",
        tab_icon: "tech-icon.png",
        content:
          activeParentTabKey === "custom_tabs" ? (
            <CustomTabs active_tab_key={activeParentTabKey} />
          ) : null,
      },
    ],
    [activeParentTabKey, accountContactList, account_contacts_loading]
  );

  // Filter out tabs for the main menu
  let filteredTabs = [];
  if (clientName == "NIIT") {
    filteredTabs = tabs.filter(
      (tab) => !["financial", "custom_tabs"].includes(tab.eventKey)
    );
  } else {
    filteredTabs = tabs.filter(
      (tab) => !["financial", "custom_tabs"].includes(tab.eventKey)
    );
  }

  // Memoized tab change handler
  const handleTabChange = useCallback((key) => {
    if (
      [
        "overview",
        "growth",
        "nudges_and_events",
        "contacts",
        // "financial",
        "opportunities",
      ].includes(key)
    ) {
      setParentActiveTab(key);
    } else {
      dispatch(setGlobalValue(key));
      setParentActiveTab("custom_tabs");
    }
  }, []);

  return (
    <div className="tabs_with_logo">
      <TabContainer
        defaultActiveKey="overview"
        activeKey={activeParentTabKey}
        onSelect={handleTabChange}
      >
        <div className="tabs_new_block">
          <div className="container web-d-flex mob_block">
            <div className="top_left_logo">
              <Nav.Item className="d-flex align-items-center">
                {account_info_loading ? (
                  <CompanyLogoSkeleton />
                ) : (
                  <>
                    <span className="logo_span">
                      <img
                        src={`${COMPANY_LOGO_BASE_URL}${encodeURIComponent(
                          account_info?.name
                        )}`}
                        alt="icons"
                        className="compnay_image_subnav"
                        loading="lazy"
                      />
                    </span>
                    <span
                      className="compnay_name_tabs"
                      title={account_info?.display_name || account_info?.name}
                    >
                      {account_info?.display_name || account_info?.name}
                    </span>
                  </>
                )}
              </Nav.Item>
            </div>
            <div className="custom-ul-wrapper">
              <Nav variant="tabs">
                {filteredTabs.map((tab, index) => (
                  <Nav.Item key={index}>
                    <Nav.Link
                      eventKey={tab.eventKey}
                      onClick={() => handleTabChange(tab.eventKey)}
                    >
                      <span className="custom_tab_span">
                        {tab.tab_icon && (
                          <span className="mr-1">
                            <img
                              src={`/img/icons/${tab.tab_icon}`}
                              alt="icons"
                              className="icons_nav"
                              loading="lazy"
                            />
                          </span>
                        )}
                        {tab.title}
                      </span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
                {clientName == "West Pharma" &&
                  dynamicTabsData.map((dynamicTab, dindex) => {
                    return (
                      <Nav.Item className="more-dropdown">
                        {/* <div className="submenu"> */}
                        <Nav.Link
                          eventKey={dynamicTab.tab_slug}
                          onClick={() => handleTabChange(dynamicTab.tab_slug)}
                        >
                          <span className="custom_tab_span">
                            {dynamicTab.tab_name}{" "}
                          </span>
                        </Nav.Link>
                        {/* </div> */}
                      </Nav.Item>
                    );
                  })}
                {/* More Dropdown */}
                <Nav.Item className="more-dropdown">
                  <Nav.Link className="more-link">
                    <span className="mr-1">
                      <img
                        src="/img/icons/more.svg"
                        alt="icons"
                        className="icons_nav"
                        loading="lazy"
                      />
                    </span>
                    More
                  </Nav.Link>
                  {/* <div className="submenu">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="financial"
                        onClick={() => handleTabChange("financial")}
                      >
                        Financials
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="opportunities"
                        onClick={() => handleTabChange("opportunities")}
                      >
                        Opportunities
                      </Nav.Link>
                    </Nav.Item>

                    {clientName != "West Pharma" &&
                      dynamicTabsData.map((dynamicTab, dindex) => {
                        return (
                          <Nav.Item>
                            <Nav.Link
                              eventKey={dynamicTab.tab_slug}
                              onClick={() =>
                                handleTabChange(dynamicTab.tab_slug)
                              }
                            >
                              {dynamicTab.tab_name}
                            </Nav.Link>
                          </Nav.Item>
                        );
                      })}
                  </div> */}
                </Nav.Item>
              </Nav>
            </div>
          </div>
        </div>
        <TabContent>
          {tabs.map((tab, index) => (
            <TabPane eventKey={tab.eventKey} key={index}>
              {tab.content}
            </TabPane>
          ))}
        </TabContent>
      </TabContainer>
    </div>
  );
}

export default React.memo(AccountDetailsTabs);
